import React, { useState, useEffect } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import './CreditSale.css';

const STATUSES = ['All', 'Pending', 'Partial', 'Paid'];

export default function CreditSale() {
  const { user, can, canAccess } = useAuth();
  const isAdmin = can('credit_sales', 'approve');
  const [view, setView] = useState('Parties'); // Toggle between 'Parties' and 'Transactions'
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCustId, setFilterCustId] = useState(''); // Store actual customer_id for drill-down
  const [searchQuery, setSearchQuery] = useState(''); // For party search box

  // Main data fetch
  const { data, loading, error, reload } = useApi(() => api.creditSales.summary({
    date: filterDate,
    status: filterStatus === 'All' ? '' : filterStatus,
    customer_id: filterCustId
  }), [filterDate, filterStatus, filterCustId]);

  const { data: products } = useApi(() => api.products.list(), []);

  const transactions = data?.transactions || [];
  const customers = data?.customers || [];

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    sale_date: today(),
    customer_id: '',
    qty: '',
    rate: '',
    amount: '',
    product_type: '',
    remarks: ''
  });

  // Set default product when products load
  useEffect(() => {
    if (products?.length > 0 && !form.product_type) {
      setForm(f => ({ ...f, product_type: products[0].code }));
    }
  }, [products]);

  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [payAmt, setPayAmt] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  if (!canAccess('credit_sales')) {
    return (
      <div className="credit-sale-page">
        <ErrBanner msg="Access Denied: You do not have permission to access credit sales." />
      </div>
    );
  }

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const handleSaveEntry = async () => {
    if (!form.customer_id || !form.amount) { notify('❌ Customer and Amount required'); return; }
    if (form.qty && form.rate && Math.abs((form.qty * form.rate) - form.amount) > 1) {
      // Optional: validate if amount matches qty*rate if both are provided
    }
    setSaving(true);
    try {
      if (editRow) {
        await api.creditSales.update(editRow.id, form);
        notify('✅ Credit entry updated');
      } else {
        await api.creditSales.create(form);
        notify('✅ Credit entry saved');
      }
      setShowForm(false);
      setEditRow(null);
      setForm({ sale_date: today(), customer_id: '', qty: '', rate: '', amount: '', product_type: 'Petrol', remarks: '' });
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.creditSales.remove(confirmDelete.id);
      notify('✅ Entry deleted');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmDelete(null);
  };

  const collect = async (id) => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) { notify('❌ Enter valid amount'); return; }
    try {
      await api.creditSales.collect(id, amt);
      notify('✅ Payment collected');
      setShowPayment(null); setPayAmt('');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  const totalOutstanding = (customers || []).reduce((s, c) => s + Number(c.balance || 0), 0);

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      sale_date: row.sale_date,
      customer_id: row.customer_id,
      qty: row.qty || '',
      rate: row.rate || '',
      amount: row.amount,
      product_type: row.product_code || 'Petrol',
      remarks: row.remarks || ''
    });
    setShowForm(true);
  };

  const selectCustomer = (id) => {
    setFilterCustId(id);
    setView('Transactions');
  };

  const clearFilters = () => {
    setFilterCustId('');
    setFilterDate('');
    setFilterStatus('All');
  };

  return (
    <div className="credit-sale-page">
      {/* Standard Header Pattern */}
      <div className="page-header">
        <h2 className="page-title">Credit Ledger</h2>
        <div className="header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="outstanding-badge" style={{ fontSize: 13, background: '#fff', border: '1px solid #e2e8f0', padding: '5px 12px', borderRadius: 6, color: '#64748b' }}>
            Net Outstanding: <strong style={{ color: '#dc2626' }}>₹{fmtNum(totalOutstanding)}</strong>
          </div>
          {can('credit_sales', 'create') && (

            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setEditRow(null); setForm({ sale_date: today(), customer_id: '', qty: '', rate: '', amount: '', product_type: 'Petrol', remarks: '' }); }}>
              {showForm ? '✕ Close' : '＋ New Credit Entry'}
            </button>
          )}
        </div>
      </div>

      {msg && <div style={{ padding: '8px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{msg}</div>}

      {/* --- ENTRY / EDIT FORM --- */}
      {showForm && (
        <div className="section-card">
          <div className="section-header">{editRow ? '✏️ Edit Transaction' : 'Record New Credit Sale'}</div>
          <div className="form-row" style={{ padding: '16px 16px 0' }}>
            <div className="form-group" style={{ flex: 1 }}><label>Date</label>
              <input type="date" className="form-control" value={form.sale_date} onChange={e => setForm({ ...form, sale_date: e.target.value })} disabled={!!editRow} />
            </div>
            <div className="form-group" style={{ flex: 2 }}><label>Customer</label>
              <select className="form-control" value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })} disabled={!!editRow}>
                <option value="">-- Select Customer --</option>
                {customers.map(c => (
                  <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>Product</label>
              <select className="form-control" value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })}>
                <option value="">-- Select Product --</option>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>Qty</label>
              <input type="number" className="form-control" value={form.qty}
                onChange={e => {
                  const q = e.target.value;
                  const amt = q && form.rate ? (parseFloat(q) * parseFloat(form.rate)).toFixed(2) : form.amount;
                  setForm({ ...form, qty: q, amount: amt });
                }} placeholder="0.000" />
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>Rate (₹)</label>
              <input type="number" className="form-control" value={form.rate}
                onChange={e => {
                  const r = e.target.value;
                  const amt = form.qty && r ? (parseFloat(form.qty) * parseFloat(r)).toFixed(2) : form.amount;
                  setForm({ ...form, rate: r, amount: amt });
                }} placeholder="0.00" />
            </div>
            <div className="form-group" style={{ flex: 1 }}><label>Amount (₹)</label>
              <input type="number" className="form-control" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
            </div>
          </div>
          <div className="form-row" style={{ padding: '0 16px 16px' }}>
            <div className="form-group" style={{ flex: 1 }}><label>Remarks / Notes</label>
              <input type="text" className="form-control" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="Vehicle number, driver name, etc." />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={handleSaveEntry} disabled={saving}>{saving ? 'Saving...' : '💾 Save Entry'}</button>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditRow(null); }}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* --- View Switcher & Filters (Integrated) --- */}
      <div className="section-card" style={{ marginBottom: 16 }}>
        <div className="form-row" style={{ padding: 16, alignItems: 'center', gap: 16 }}>
          <div className="view-toggle-mini" style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
            <button className={`btn-toggle ${view === 'Parties' ? 'active' : ''}`} onClick={() => setView('Parties')}>Parties</button>
            <button className={`btn-toggle ${view === 'Transactions' ? 'active' : ''}`} onClick={() => setView('Transactions')}>Transactions</button>
          </div>

          <div style={{ borderLeft: '1px solid #e2e8f0', height: 24 }} />

          {view === 'Parties' ? (
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <input className="form-control" placeholder="🔍 Search party name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ height: 38 }} />
            </div>
          ) : (
            <>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <select className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ height: 38 }}>
                  {STATUSES.map(s => <option key={s} value={s}>{s} Bills</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <input type="date" className="form-control" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={{ height: 38 }} />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <select className="form-control" value={filterCustId} onChange={e => setFilterCustId(e.target.value)} style={{ height: 38 }}>
                  <option value="">All Customers</option>
                  {customers.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Reset</button>
            </>
          )}
        </div>
      </div>

      {view === 'Parties' ? (
        /* --- PARTIES VIEW (CARDS) --- */
        <div className="party-grid">
          {customers.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map(c => (
            <div key={c.customer_id} className="party-card-mini" onClick={() => selectCustomer(c.customer_id)}>
              <div className="p-header">
                <div className="p-name">{c.name}</div>
                <div className="p-badge">{c.open_bills} Bills</div>
              </div>
              <div className="p-body">
                <div className="p-stat">
                  <label>OUTSTANDING</label>
                  <div className="p-val danger">₹{fmtNum(c.balance)}</div>
                </div>
                <div className="p-stat">
                  <label>CREDIT LIMIT</label>
                  <div className="p-val">₹{fmtNum(c.credit_limit)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* --- TRANSACTIONS VIEW (TABLE) --- */
        <div className="section-card">
          <div className="section-header">Credit Transaction Records</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Bill No</th>
                  <th style={{ textAlign: 'left' }}>Customer</th>
                  <th style={{ textAlign: 'left' }}>Product</th>
                  <th className="right-align">Qty</th>
                  <th className="right-align">Rate</th>
                  <th className="right-align">Amount</th>
                  <th className="right-align">Paid</th>
                  <th className="right-align">Balance</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="10"><Spinner /></td></tr> : transactions.length === 0 ? (
                  <tr><td colSpan="10" style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>No transactions found.</td></tr>
                ) : transactions.map((t, idx) => (
                  <tr key={t.id}>
                    <td>{idx + 1}</td>
                    <td>{fmtDate(t.sale_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{t.bill_no}</td>
                    <td>{t.party_name}</td>
                    <td><span className="badge-outline">{t.product_code}</span></td>
                    <td className="right-align">{t.qty || '0.000'}</td>
                    <td className="right-align">₹{fmtNum(t.rate)}</td>
                    <td className="right-align">₹{fmtNum(t.amount)}</td>
                    <td className="right-align" style={{ color: '#059669' }}>₹{fmtNum(t.paid)}</td>
                    <td className="right-align" style={{ color: '#dc2626', fontWeight: 700 }}>₹{fmtNum(t.balance)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(() => {
                        const amt = Number(t.amount);
                        const paid = Number(t.paid);
                        const bal = Number(t.balance);

                        let displayStatus = t.status;
                        if (bal <= 0) displayStatus = 'Paid';
                        else if (paid > 0) displayStatus = 'Partial';
                        else displayStatus = 'Pending';

                        return <span className={`status-badge ${displayStatus.toLowerCase()}`}>{displayStatus}</span>;
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button className="btn-icon-sm collect" title="Collect Payment" onClick={() => { setShowPayment(t.id); setPayAmt(''); }} disabled={t.status === 'Paid'}>💳</button>
                        {(isAdmin || t.status !== 'Paid') && (
                          <button className="btn-icon-sm edit" title="Edit" onClick={() => openEdit(t)}>✏️</button>
                        )}
                        {(isAdmin || t.status !== 'Paid') && (
                          <button className="btn-icon-sm delete" title="Delete" onClick={() => setConfirmDelete(t)}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && (() => {
        const row = transactions.find(t => t.id === showPayment);
        if (!row) return null;
        const remaining = Number(row.balance) - (Number(payAmt) || 0);

        return (
          <div className="cs-modal-overlay" onClick={() => setShowPayment(null)}>
            <div className="cs-modal-card" onClick={e => e.stopPropagation()}>
              <div className="cs-modal-header">
                <h3>Collect Payment</h3>
                <button className="cs-close-btn" onClick={() => setShowPayment(null)}>✕</button>
              </div>

              <div className="cs-modal-body">
                <div className="cs-info-grid">
                  <div className="cs-info-tile">
                    <label>Customer</label>
                    <div className="cs-info-text">{row.party_name}</div>
                  </div>
                  <div className="cs-info-tile">
                    <label>Bill Number</label>
                    <div className="cs-info-text mono">{row.bill_no}</div>
                  </div>
                </div>

                <div className="cs-balance-highlight">
                  <label>Amount Due</label>
                  <div className="cs-balance-amt">₹{fmtNum(row.balance)}</div>
                </div>

                <div className="cs-amount-input-wrap">
                  <label>Amount to Collect (₹)</label>
                  <input
                    type="number"
                    className="cs-input-xl"
                    placeholder="0.00"
                    value={payAmt}
                    onChange={e => setPayAmt(e.target.value)}
                    autoFocus
                  />
                  {payAmt > 0 && (
                    <div className={`cs-remaining-tag ${remaining <= 0 ? 'fully-paid' : ''}`}>
                      {remaining <= 0 ? '✓ Full Payment' : `Balance left: ₹${fmtNum(remaining)}`}
                    </div>
                  )}
                </div>
              </div>

              <div className="cs-modal-footer">
                <button className="btn btn-primary btn-full" onClick={() => collect(showPayment)} disabled={saving || !payAmt}>
                  {saving ? 'Processing...' : 'Confirm Payment'}
                </button>
                <button className="btn btn-secondary btn-full" onClick={() => setShowPayment(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Delete Transaction"
        message={`Are you sure you want to delete ${confirmDelete?.bill_no}? This will reverse the customer balance.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
